import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { designSystem, Modal } from '@yola/ws-ui';
import { constants } from '@yola/subscription-manager-js';
import planNameWithReinforcement from '../../common/constants/plan-name-with-reinforcement';
import useMatchBreakpoint from '../../common/hooks/use-match-breakpoint';
import { t } from '../../localization';
import configStorage from '../../configuration/config-storage';
import prefetchResource from '../../common/helpers/prefetch-resource';
import breakpoints from '../../common/constants/breakpoints';
import { CUSTOMERS_AMOUNT } from '../common/constants/common';
import avatars from '../common/constants/avatars';
import AvatarList from '../common/components/avatar-list';
import Rating from '../common/components/rating';
import formatPrice from '../common/helpers/format-price';
import ReviewBanner from './components/review-banner';
import usePrices from './hooks/use-prices';

const { MONTHLY, ANNUAL } = constants.SubscriptionTerm;
const {
  SubscriptionType: { B2C_HOSTING_GOLD },
} = constants;

const {
  ActionButton,
  Box,
  Tag,
  Heading,
  Paragraph,
  Stack,
  HorizontalStack,
  SolidIcon,
  Popover,
  PopoverTrigger,
  PopoverContent,
} = designSystem;

const LARGE_MODAL_WIDTH = 900;
const MEDIUM_MODAL_WIDTH = 520;

function calculatePrice({ prices, term, currency = 'USD', locale }) {
  if (!prices.gold) {
    return 0;
  }

  return formatPrice(prices.gold[term].prices.current[currency], locale, currency);
}

const DifmUpsellDialog = ({
  partnerId,
  term = MONTHLY,
  currency = 'USD',
  isUpgradeFlow = false,
  shouldReinforcePlanNames,
  onSubmit,
  onClose,
}) => {
  const isMediumScreen = useMatchBreakpoint(breakpoints.SM);
  const isLargeScreen = useMatchBreakpoint(breakpoints.LG);
  const isFullScreen = !isMediumScreen && !isLargeScreen;
  const { cdnUrl, locale = 'en' } = configStorage.get();
  const popoverVideoSrc = `${cdnUrl}/difm/videos/hong-kong-en-hk-live-overview-d7482413.mp4`;
  const isLargeOrMediumScreen = isLargeScreen || isMediumScreen;

  // This is a temporary flag to hide the GMB service
  const isGMBServiceVisible = false;

  useEffect(() => {
    if (isGMBServiceVisible) {
      prefetchResource(popoverVideoSrc);
    }
    // exclude the dependency array to ensure this effect runs only once
    // eslint-disable-next-line yola/react-hooks/exhaustive-deps
  }, []);

  const avatarImages = useMemo(
    () =>
      [
        `${cdnUrl}${avatars.AVATAR_1}`,
        `${cdnUrl}${avatars.AVATAR_2}`,
        `${cdnUrl}${avatars.AVATAR_3}`,
        `${cdnUrl}${avatars.AVATAR_4}`,
        `${cdnUrl}${avatars.AVATAR_5}`,
      ].slice(0, isLargeOrMediumScreen ? 5 : 3),
    [isLargeOrMediumScreen, cdnUrl]
  );

  const { data: prices, isLoading } = usePrices({ partnerId });
  const price = useMemo(
    () => calculatePrice({ prices, term, currency, locale }),
    [prices, term, currency, locale]
  );
  const goldPlanName = shouldReinforcePlanNames
    ? planNameWithReinforcement[B2C_HOSTING_GOLD]
    : B2C_HOSTING_GOLD;

  const prePriceLabel = isUpgradeFlow
    ? t('Upgrade to {planName}', {
        planName: goldPlanName,
      })
    : t('Get started with {planName}', {
        planName: goldPlanName,
      });
  const priceLabel = term === ANNUAL ? t('{price}/yr*', { price }) : t('{price}/mo*', { price });
  const actionButtonLabel = `${prePriceLabel} - ${priceLabel}`;

  return (
    <Modal
      key={isLargeScreen ? 'large' : 'small'}
      isBodyScrollDisabled
      isContainerScrollable
      centered
      width={isLargeScreen ? LARGE_MODAL_WIDTH : MEDIUM_MODAL_WIDTH}
      height="auto"
      resizable={false}
      draggable={false}
      fullscreen={isFullScreen}
      overlay="visible"
      className={classNames('ws-difm-upsell-dialog', {
        'ws-difm-upsell-dialog--fullscreen': isFullScreen,
      })}
    >
      {onClose && (
        <div className="ws-difm-upsell-dialog__close-button">
          <ActionButton format="text" iconGlyph="close" onClick={onClose} />
        </div>
      )}

      <div className="ws-difm-upsell-dialog__container">
        <div className="ws-difm-upsell-dialog__left-column">
          <Box
            paddingTop="spacing-3-xl"
            paddingRight="spacing-xs"
            paddingLeft="spacing-xs"
            breakpoints={{
              xs: {
                paddingTop: 'spacing-3-xl',
                paddingRight: 'spacing-m',
                paddingLeft: 'spacing-m',
              },
              sm: {
                paddingTop: 'spacing-2-xl',
                paddingRight: 'spacing-xl',
                paddingLeft: 'spacing-xl',
              },
            }}
          >
            <Stack gap="spacing-2-xs">
              <div>
                <Tag
                  label={t('PROFESSIONAL SERVICES')}
                  appearance="accent-secondary"
                  size="small"
                />
              </div>

              <Heading type="heading-2">
                {t('Let us translate your vision into a professional website')}
              </Heading>

              <Paragraph>
                {t(
                  'Our designers are ready to bring your ideas to life with a website that looks great and fits your budget'
                )}
              </Paragraph>

              <HorizontalStack gap="spacing-2-xs" verticalAlign="center">
                <AvatarList images={avatarImages} size={40} />
                <Stack>
                  <Rating stars={5} size={16} />

                  <Paragraph size="small" appearance="medium-emphasis">
                    {t('Trusted by {customers}+ happy customers', {
                      customers: CUSTOMERS_AMOUNT,
                    })}
                  </Paragraph>
                </Stack>
              </HorizontalStack>
            </Stack>

            <Box paddingTop="spacing-m">
              <Stack gap="spacing-2-xs">
                <HorizontalStack gap="spacing-2-xs">
                  <div>
                    <SolidIcon glyph="check" type="success" size="16" />
                  </div>
                  <Heading type="heading-6">
                    {t('Get a professional website, built and managed for you')}
                  </Heading>
                </HorizontalStack>

                {isGMBServiceVisible && (
                  <HorizontalStack gap="spacing-2-xs">
                    <div>
                      <SolidIcon glyph="check" type="success" size="16" />
                    </div>
                    <Heading type="heading-6">
                      {t('Stand out on Google with a premium')}{' '}
                      <Popover
                        appearance="dark"
                        mode="tooltip"
                        placement={isLargeOrMediumScreen ? 'right-center' : 'bottom-right'}
                        width={240}
                      >
                        <PopoverTrigger>
                          <span className="ws-difm-upsell-dialog__popover-trigger">
                            {t('Business Profile')}
                          </span>
                        </PopoverTrigger>
                        <PopoverContent>
                          <Stack gap="spacing-3-xs">
                            <Heading type="heading-6" appearance="white">
                              {t('Google Business Profile')}
                            </Heading>

                            <Paragraph appearance="low-emphasis" size="small">
                              {t(
                                'Turn people who find you on Google Search and Maps into new customers with a premium Business Profile.'
                              )}
                            </Paragraph>
                          </Stack>
                          <Box paddingTop="spacing-2-xs">
                            <video
                              className="ws-difm-upsell-dialog__video"
                              autoPlay
                              loop
                              muted
                              playsInline
                            >
                              <source src={popoverVideoSrc} type="video/mp4" />
                              Your browser does not support the video tag.
                            </video>
                          </Box>
                        </PopoverContent>
                      </Popover>
                    </Heading>
                  </HorizontalStack>
                )}

                <HorizontalStack gap="spacing-2-xs">
                  <div>
                    <SolidIcon glyph="check" type="success" size="16" />
                  </div>
                  <Heading type="heading-6">{t('You share ideas, we handle the rest')}</Heading>
                </HorizontalStack>

                <HorizontalStack gap="spacing-2-xs">
                  <div>
                    <SolidIcon glyph="check" type="success" size="16" />
                  </div>
                  <Heading type="heading-6">
                    {t('With unlimited revisions')}
                    {', '}
                    <Popover
                      appearance="dark"
                      mode="tooltip"
                      placement={isLargeOrMediumScreen ? 'top-center' : 'bottom-center'}
                      width={278}
                    >
                      <PopoverTrigger>
                        <span className="ws-difm-upsell-dialog__popover-trigger">
                          {t('benefits of {planName}', {
                            planName: goldPlanName,
                          })}
                        </span>
                      </PopoverTrigger>
                      <PopoverContent>
                        <Stack gap="spacing-2-xs">
                          <Heading type="heading-6" appearance="white">
                            {t('What comes with {planName}?', {
                              planName: goldPlanName,
                            })}
                          </Heading>

                          <Stack gap="spacing-3-xs">
                            <HorizontalStack gap="spacing-2-xs">
                              <div>
                                <SolidIcon glyph="check" type="success" size="12" strokeWidth={2} />
                              </div>
                              <Paragraph type="heading-6" appearance="white" size="small">
                                {t('Host your site with no limits')}
                              </Paragraph>
                            </HorizontalStack>

                            <HorizontalStack gap="spacing-2-xs">
                              <div>
                                <SolidIcon glyph="check" type="success" size="12" strokeWidth={2} />
                              </div>
                              <Paragraph type="heading-6" appearance="white" size="small">
                                {t('AI-powered assistance')}
                              </Paragraph>
                            </HorizontalStack>

                            <HorizontalStack gap="spacing-2-xs">
                              <div>
                                <SolidIcon glyph="check" type="success" size="12" strokeWidth={2} />
                              </div>
                              <Paragraph type="heading-6" appearance="white" size="small">
                                {t('Priority support from real people')}
                              </Paragraph>
                            </HorizontalStack>

                            <HorizontalStack gap="spacing-2-xs">
                              <div>
                                <SolidIcon glyph="check" type="success" size="12" strokeWidth={2} />
                              </div>
                              <Paragraph type="heading-6" appearance="white" size="small">
                                {t('Full access to all design tools')}
                              </Paragraph>
                            </HorizontalStack>

                            <HorizontalStack gap="spacing-2-xs">
                              <div>
                                <SolidIcon glyph="check" type="success" size="12" strokeWidth={2} />
                              </div>
                              <Paragraph type="heading-6" appearance="white" size="small">
                                {t('Premium blocks, icons, and styling')}
                              </Paragraph>
                            </HorizontalStack>
                          </Stack>

                          <Paragraph appearance="low-emphasis" size="small">
                            <i>
                              {t(
                                '{planName} is for those who want more possibilities and confidence in the result',
                                {
                                  planName: goldPlanName,
                                }
                              )}
                            </i>
                          </Paragraph>
                        </Stack>
                      </PopoverContent>
                    </Popover>
                    {', '}
                    {t('and 24/7 support')}
                  </Heading>
                </HorizontalStack>
              </Stack>
            </Box>
          </Box>

          <Box
            paddingTop="spacing-l"
            paddingRight="spacing-xs"
            paddingLeft="spacing-xs"
            paddingBottom="spacing-m"
            breakpoints={{
              xs: {
                paddingTop: 'spacing-3-xl',
                paddingRight: 'spacing-m',
                paddingLeft: 'spacing-m',
                paddingBottom: 'spacing-m',
              },
              sm: {
                paddingTop: 'spacing-l',
                paddingRight: 'spacing-xl',
                paddingLeft: 'spacing-xl',
                paddingBottom: 'spacing-2-xl',
              },
            }}
          >
            <Stack gap="spacing-2-xs">
              <ActionButton
                appearance="cta"
                format="solid"
                fullWidth
                isLoading={isLoading}
                onClick={onSubmit}
                label={actionButtonLabel}
              />
              <Paragraph size="small" appearance="medium-emphasis" align="center">
                {t('* 30 day money back guarantee applies. Cancel anytime')}
              </Paragraph>
            </Stack>
          </Box>
        </div>

        {isLargeScreen && (
          <div className="ws-difm-upsell-dialog__right-column">
            <div
              className="ws-difm-upsell-dialog__background"
              style={{
                backgroundImage: `url(${cdnUrl}/difm/images/gradient-backgound-eb3b2079.png)`,
              }}
            >
              <img
                className="ws-difm-upsell-dialog__preview-image"
                src={`${cdnUrl}/difm/images/site-preview-a7d2f91c.jpg`}
                alt="1"
              />
              <div className="ws-difm-upsell-dialog__banner-container">
                <ReviewBanner
                  reviewerName="Monica Crawford"
                  reviewText={t(
                    "Thank you so much for this service. You've helped make my dream feel a little more real. I'm just starting out and had no idea where to begin - this was exactly the boost I needed"
                  )}
                  avatarUrl={`${cdnUrl}/difm/images/review-avatar-cfc089b9.png`}
                  stars={5}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

DifmUpsellDialog.propTypes = {
  partnerId: PropTypes.string.isRequired,
  term: PropTypes.string,
  currency: PropTypes.string,
  isUpgradeFlow: PropTypes.bool,
  shouldReinforcePlanNames: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

export default DifmUpsellDialog;
